import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkingMode } from '../../../contexts/WorkingModeContext';
import { useConfirm } from '../../../contexts/ConfirmContext';
import CustomDropdown, { DropdownOption } from '../../UI/CustomDropdown';
import SidePanel from '../../UI/SidePanel';
import FloatingWorkingMode from '../../UI/FloatingWorkingMode';
import TaskForm, { TaskFormData } from './components/TaskForm';
import TaskInitiationForm from './components/TaskInitiationForm';
import { Task, TaskFilters, TaskStats } from './types';
import TaskStatsComponent from './components/TaskStats';
import { InitiativeList } from './Initiatives';
import ProposalDetailsPanel from './components/ProposalDetailsPanel';

// ... (existing imports)
import TaskList from './components/TaskList';
import { calculateStats, fetchProposalRequests } from './components/helpers';
import { UserPermission } from '../../../types';
import SkeletonLoader from '../../UI/SkeletonLoader';
import styles from '../../../styles/TaskManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import searchStyles from '../../../styles/SearchBar.module.css';
import customDropdownStyles from '../../../styles/CustomDropdown.module.css';
import sidePanelStyles from '../../../styles/SidePanel.module.css';
import skeletonStyles from '../../../styles/SkeletonLoader.module.css';
import taskFormStyles from '../../../styles/TaskForm.module.css';

interface TaskManagementProps {
  embedded?: boolean; // When embedded, force personal mode and remove fixed header
  userId?: string; // Optional userId to view tasks for a specific user (for admins)
}

const TaskManagement: React.FC<TaskManagementProps> = ({ embedded = false, userId: targetUserId }) => {
  const navigate = useNavigate();
  const notificationService = useNotification();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const { user } = useAuth();
  const { isWorkMode: contextWorkMode } = useWorkingMode();
  const confirmService = useConfirm();

  // Force personal mode when embedded
  const isWorkMode = embedded ? false : contextWorkMode;

  // Use targetUserId if provided (for viewing another user's tasks), otherwise use logged-in user's ID
  const effectiveUserId = targetUserId || user?.id;

  const [filters, setFilters] = useState<TaskFilters>({
    search: '',
    status: '',
    priority: '',
    category: '',
    group: '',
    due_date: '',
    personal_tab: 'assigned'
  });

  const MANAGEMENT_DESIGNATIONS = useMemo(() => [
    'chairman',
    'vice_chairman',
    'director',
    'principal',
    'vice_principal',
    'dean',
    'head_of_department'
  ], []);

  const isManagementRole = useMemo(() => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!user.designation) return false;
    return MANAGEMENT_DESIGNATIONS.includes(user.designation.toLowerCase());
  }, [user, MANAGEMENT_DESIGNATIONS]);

  // Personal mode tab state
  const [activePersonalTab, setActivePersonalTab] = useState<'assigned' | 'approvals' | 'initiatives' | 'requests'>('assigned');

  // Initiative Filter State
  const [initiativeFilter, setInitiativeFilter] = useState<'initiatives' | 'proposals'>('initiatives');
  const [showInitiativeMenu, setShowInitiativeMenu] = useState(false);
  const initiativeMenuRef = useRef<HTMLDivElement>(null);

  // Security: Redirect if user is on requests tab but lacks permission
  useEffect(() => {
    if (activePersonalTab === 'requests' && !isManagementRole) {
      setActivePersonalTab('assigned');
      setFilters(prev => ({ ...prev, personal_tab: 'assigned' }));
    }
  }, [activePersonalTab, isManagementRole]);

  // Work mode tab state
  const [activeWorkTab, setActiveWorkTab] = useState<'tasks' | 'templates'>('tasks');

  const [tasks, setTasks] = useState<Task[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [hasEditPermissions, setHasEditPermissions] = useState(false);
  const [hasEditDeletePermissions, setHasEditDeletePermissions] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [editingTaskDetails, setEditingTaskDetails] = useState<any>(null);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
  const [isLoadingTaskDetails, setIsLoadingTaskDetails] = useState(false);

  // Proposal Panel State
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(null);
  const [showProposalPanel, setShowProposalPanel] = useState(false);

  // Task creation form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [groups, setGroups] = useState<any[]>([]);
  const [taskCategories, setTaskCategories] = useState<any[]>([]);
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  // Hierarchy-based assignable users for work mode
  const [hierarchyAssignableUsers, setHierarchyAssignableUsers] = useState<any[]>([]);
  const [isLoadingHierarchyUsers, setIsLoadingHierarchyUsers] = useState(false);
  const [selectedAssigneesForGroup, setSelectedAssigneesForGroup] = useState<string[]>([]);
  const [availableGroupsFromHierarchy, setAvailableGroupsFromHierarchy] = useState<any[]>([]);

  // Template instance creation state (for Personal mode)
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<Task[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Get status options based on active tab
  const getStatusOptions = () => {
    const isApprovalsTab = !isWorkMode && (activePersonalTab === 'approvals');
    if (isApprovalsTab) {
      // For approvals tab, show approval-specific statuses
      return [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending Approval' },
        { value: 'in_progress', label: 'Under Review' },
        { value: 'completed', label: 'Approved' },
        { value: 'cancelled', label: 'Rejected' }
      ];
    }
    return [
      { value: '', label: 'All Status' },
      { value: 'pending', label: 'Pending' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'on_hold', label: 'On Hold' }
    ];
  };

  const statusOptions = getStatusOptions();

  const priorityOptions = [
    { value: '', label: 'All Priority' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];


  const loadTasks = async (isModeSwitch: boolean = false, overrideTab?: 'assigned' | 'approvals' | 'initiatives' | 'requests') => {
    if (isLoadingTasks) {
      return;
    }

    setIsLoadingTasks(true);
    setLoading(true);

    if (!isModeSwitch) {
      showLoader('Loading tasks...');
    }

    const startTime = Date.now();
    const minLoadingTime = isModeSwitch ? 1000 : 0;

    try {
      // Use overrideTab if provided (for immediate tab changes), otherwise use activePersonalTab state
      const currentTab = overrideTab !== undefined ? overrideTab : activePersonalTab;
      console.log('loadTasks executing:', { overrideTab, activePersonalTab, resolvedTab: currentTab });
      const isApprovalsTab = !isWorkMode && (currentTab === 'approvals');

      let response;

      const commonFilters = {
        search: filters.search,
        status: filters.status,
        priority: filters.priority,
        category: filters.category,
        group: filters.group,
        due_date: filters.due_date,
        is_template: isWorkMode ? (activeWorkTab === 'templates' ? 'true' : 'false') : undefined
      };

      if (isApprovalsTab) {
        const approvalFilters = {
          ...commonFilters,
          ...(targetUserId ? { user_id: targetUserId } : {})
        };
        response = await apiService.getUserApprovalTasks(approvalFilters);
      } else if (isWorkMode) {
        // Work Mode: Created Tasks
        response = await apiService.getCreatedTasks(commonFilters);
      } else {
        // Personal Mode
        const personalFilters = {
          ...commonFilters,
          ...(targetUserId ? { user_id: targetUserId } : {})
        };

        // For assigned tab or requests tab
        if (currentTab === 'requests') {
          const data = await fetchProposalRequests(personalFilters);
          response = { status: 'success', data: data };
        } else {
          response = await apiService.getAssignedTasks(personalFilters);
        }
      }

      // Calculate remaining time to ensure minimum loading duration
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      // Wait for remaining time if needed
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      if (response && response.status === 'success') {
        let tasksData = response.data || [];
        tasksData = Array.isArray(tasksData) ? tasksData : [];

        console.log('Tasks loaded:', {
          mode: isWorkMode ? 'work' : 'personal',
          tab: currentTab,
          count: tasksData.length
        });

        setAllTasks(tasksData);
        setTasks(tasksData);
        setError(null);
        setIsInitialLoad(false);
      } else {
        setError(response?.message || 'Failed to load tasks');
        notificationService.error(response?.message || 'Failed to load tasks');
      }
    } catch (err: any) {
      console.error('Error loading tasks:', err);
      setError(err);
      notificationService.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
      setIsLoadingTasks(false);

      if (!isModeSwitch) {
        hideLoader();
      }
    }
  };

  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user) {
        setPermissionsLoading(false);
        return;
      }

      try {
        setPermissionsLoading(true);
        const response = await apiService.getUserPermissions() as any;
        if (response && response.status === 'success') {
          const responseData = response.data || response;
          const permissions = (responseData.permissions || []) as UserPermission[];
          setUserPermissions(permissions);

          const editActions = ['add', 'edit', 'delete', 'create', 'update'];
          const hasEdit = permissions.some(
            perm => perm.module === 'task_management' && editActions.includes(perm.action)
          );
          setHasEditPermissions(hasEdit);

          // Check specifically for edit/delete permissions
          const editDeleteActions = ['edit', 'delete', 'update'];
          const hasEditDelete = permissions.some(
            perm => perm.module === 'task_management' && editDeleteActions.includes(perm.action)
          );
          setHasEditDeletePermissions(hasEditDelete);
        }
      } catch (error: any) {
        console.error('Error loading user permissions:', error);
        setUserPermissions([]);
        setHasEditPermissions(false);
        setHasEditDeletePermissions(false);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadUserPermissions();
  }, [user]);

  // Click outside listener for initiative menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (initiativeMenuRef.current && !initiativeMenuRef.current.contains(event.target as Node)) {
        setShowInitiativeMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!permissionsLoading) {
      // if (!isWorkMode && activePersonalTab !== 'assigned') {
      //   console.log('Resetting tab to assigned due to mode/permissions check');
      //   setActivePersonalTab('assigned');
      //   setFilters(prev => ({ ...prev, personal_tab: 'assigned' }));
      // }
      loadTasks(true);
    }
  }, [isWorkMode]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadTasks();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters.search, filters.status, filters.priority, filters.category, filters.group, filters.due_date, activePersonalTab, activeWorkTab, isWorkMode]);

  const handleTaskClick = (task: Task) => {
    // If it's a proposal, open the side panel
    if (task.is_proposal) {
      setSelectedProposalId(task.id);
      setShowProposalPanel(true);
      return;
    }

    // If it's a template, go to instances view
    if (task.is_template || task.is_user_template || task.allow_user_instances) {
      navigate(`/task-management/${task.id}/instances`);
    } else {
      navigate(`/task-management/${task.id}`);
    }
  };

  const handleEditTask = async (task: Task) => {
    setEditingTask(task);
    setShowEditModal(true);
    setIsLoadingTaskDetails(true);

    // Load the group and related data for editing
    if (task.group?.id) {
      // Group task - load group categories and members
      setSelectedGroupId(task.group.id);
      loadTaskCategories(task.group.id);
      loadGroupMembers(task.group.id);
    } else {
      // Global task - load global categories and hierarchy assignable users
      setSelectedGroupId('');
      loadTaskCategories(undefined, 'global');
      await loadHierarchyAssignableUsers();
    }

    try {
      // Load full task details for editing
      const response = await apiService.getTaskDetails(task.id);
      if (response.status === 'success') {
        const taskData = response.data || response;

        // Debug: Log assignees to verify structure
        console.log('Task details loaded for editing:', {
          taskId: task.id,
          assignees: taskData.assignees,
          assigneeIds: taskData.assignees?.map((a: any) => a.user?.id || a.id),
          groupId: task.group?.id,
          isGlobal: !task.group?.id
        });

        setEditingTaskDetails(taskData);

        // Load existing documents (only for group tasks)
        if (task.group?.id) {
          try {
            const docsResponse = await apiService.getTaskDocuments(task.group.id, task.id);
            if (docsResponse.status === 'success') {
              const documentsData = docsResponse.data || (docsResponse as any).documents || [];
              setExistingDocuments(Array.isArray(documentsData) ? documentsData : []);
            }
          } catch (error: any) {
            console.error('Error loading task documents:', error);
            setExistingDocuments([]);
          }
        } else {
          // Global tasks don't have documents via group endpoint
          setExistingDocuments([]);
        }
      } else {
        notificationService.error('Failed to load task details');
      }
    } catch (error: any) {
      console.error('Error loading task details:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load task details');
    } finally {
      setIsLoadingTaskDetails(false);
    }
  };

  const handleDeleteTask = async (task: Task) => {
    const confirmed = await confirmService.show(
      'Delete Task',
      `Are you sure you want to delete the task "${task.name}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    showLoader('Deleting task...');
    try {
      // Check if this is a global task (no group) or a group task
      const isGlobalTask = !task.group?.id;
      const response = isGlobalTask
        ? await apiService.deleteGlobalTask(task.id)
        : await apiService.deleteTask(task.id, task.group.id);

      if (response.status === 'success') {
        notificationService.success('Task deleted successfully');
        loadTasks();
      } else {
        notificationService.error(response.message || 'Failed to delete task');
      }
    } catch (error: any) {
      console.error('Error deleting task:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to delete task. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleFilterChange = (key: keyof TaskFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handlePersonalTabChange = (tab: 'assigned' | 'approvals' | 'initiatives' | 'requests') => {
    console.log('Tab change requested:', tab);
    setActivePersonalTab(tab);
    setFilters(prev => ({ ...prev, personal_tab: tab as any }));
    if (tab !== 'initiatives') {
      loadTasks(true, tab);
    }
  };

  // Load groups for task creation
  const loadGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const response = await apiService.getGroups();
      if (response.status === 'success') {
        setGroups(response.data || []);
      }
    } catch (error: any) {
      console.error('Error loading groups:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load groups');
    } finally {
      setIsLoadingGroups(false);
    }
  };
  const loadTaskCategories = async (groupId?: string, filter: 'global' | 'group' | 'all' = 'global') => {
    setIsLoadingCategories(true);
    try {
      const response = await apiService.getTaskCategories(groupId, filter);
      if (response.success) {
        setTaskCategories(response.categories || []);
      } else {
        notificationService.error(response.message || 'Failed to load task categories');
      }
    } catch (error: any) {
      console.error('Error loading task categories:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load task categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const loadGroupMembers = async (groupId: string) => {
    if (!groupId) {
      setGroupMembers([]);
      return;
    }
    setIsLoadingMembers(true);
    try {
      const response = await apiService.getGroupMembers(groupId);
      if (response.status === 'success') {
        setGroupMembers(response.data || []);
      }
    } catch (error: any) {
      console.error('Error loading group members:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load group members');
    } finally {
      setIsLoadingMembers(false);
    }
  };

  // Load hierarchy-based assignable users for work mode
  const loadHierarchyAssignableUsers = async () => {
    setIsLoadingHierarchyUsers(true);
    try {
      const response = await apiService.getHierarchyAssignableUsers();
      if (response.status === 'success') {
        const assignableUsers = response.data?.assignable_users || [];
        setHierarchyAssignableUsers(assignableUsers);
      } else {
        notificationService.error(response.message || 'Failed to load assignable users');
        setHierarchyAssignableUsers([]);
      }
    } catch (error: any) {
      console.error('Error loading hierarchy assignable users:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load assignable users');
      setHierarchyAssignableUsers([]);
    } finally {
      setIsLoadingHierarchyUsers(false);
    }
  };

  const handleGroupChange = (groupId: string) => {
    setSelectedGroupId(groupId);
    if (groupId) {
      loadTaskCategories(groupId);
      loadGroupMembers(groupId);
    } else {
      setTaskCategories([]);
      setGroupMembers([]);
    }
  };

  // Handle update task submission with files
  const handleUpdateTask = async (formData: TaskFormData, files: File[], documentMetadata: Record<string, any>) => {
    if (!editingTask) {
      notificationService.error('Cannot update task: Task information missing');
      return;
    }

    // Check if this is a global task (no group)
    const isGlobalTask = !editingTask.group?.id;

    showLoader('Updating task...');
    try {
      // Create FormData for file uploads
      const formDataObj = new FormData();

      formDataObj.append('name', formData.name.trim());
      formDataObj.append('description', formData.description || '');
      formDataObj.append('category_id', formData.category_id);
      formDataObj.append('priority', formData.priority);
      formDataObj.append('assignee_ids', JSON.stringify(formData.assignee_ids));
      formDataObj.append('due_date', formData.due_date);
      formDataObj.append('estimated_hours', formData.estimated_hours.toString());
      formDataObj.append('status', formData.status || 'pending');
      formDataObj.append('approval_type', formData.approval_type);
      formDataObj.append('requires_documents', formData.requires_documents.toString());
      formDataObj.append('enable_group_chat', formData.enable_group_chat.toString());
      formDataObj.append('distribution_method', formData.distribution_method);
      formDataObj.append('point_allocation', JSON.stringify(formData.point_allocation));
      // Work descriptions are NOT stored for template tasks
      const workDescriptions = formData.allow_user_instances ? {} : formData.work_descriptions;
      formDataObj.append('work_descriptions', JSON.stringify(workDescriptions));

      // Recurrence/Scheduler fields
      formDataObj.append('is_recurring', formData.is_recurring.toString());
      formDataObj.append('recurrence_type', formData.recurrence_type);
      if (formData.recurrence_weekly_days && formData.recurrence_weekly_days.length > 0) {
        formDataObj.append('recurrence_weekly_days', JSON.stringify(formData.recurrence_weekly_days));
      }
      if (formData.recurrence_monthly_dates && formData.recurrence_monthly_dates.length > 0) {
        formDataObj.append('recurrence_monthly_dates', JSON.stringify(formData.recurrence_monthly_dates));
      }
      if (formData.recurrence_yearly_date) {
        formDataObj.append('recurrence_yearly_date', JSON.stringify(formData.recurrence_yearly_date));
      }
      if (formData.recurrence_end_date) {
        formDataObj.append('recurrence_end_date', formData.recurrence_end_date);
      }

      // User instance creation fields
      formDataObj.append('allow_user_instances', formData.allow_user_instances.toString());
      if (formData.instance_template_name) {
        formDataObj.append('instance_template_name', formData.instance_template_name);
      }

      // Add uploaded files
      files.forEach((file, index) => {
        formDataObj.append(`document_${index}`, file);
        const metadata = documentMetadata[Object.keys(documentMetadata)[index]];
        if (metadata) {
          formDataObj.append(`document_title_${index}`, metadata.title);
          formDataObj.append(`document_description_${index}`, metadata.description);
          formDataObj.append(`document_type_${index}`, metadata.document_type);
          formDataObj.append(`document_required_${index}`, metadata.is_required.toString());
        }
      });

      // Add approver IDs if multiple approvals
      if (formData.approval_type === 'multiple' && formData.approver_ids) {
        formDataObj.append('approver_ids', JSON.stringify(formData.approver_ids));
      }

      let response;
      if (isGlobalTask) {
        // Use global task update endpoint
        response = await apiService.updateGlobalTaskWithFiles(editingTask.id, formDataObj);
      } else {
        // Use group task update endpoint
        response = await apiService.updateTaskWithFiles(editingTask.id, editingTask.group.id, formDataObj);
      }

      if (response.status === 'success') {
        notificationService.success('Task updated successfully');
        setShowEditModal(false);
        setEditingTask(null);
        setEditingTaskDetails(null);
        setExistingDocuments([]);
        setTaskCategories([]);
        setGroupMembers([]);
        loadTasks();
      } else {
        notificationService.error(response.message || 'Failed to update task');
      }
    } catch (error: any) {
      console.error('Error updating task:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to update task. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleFormSubmit = async (formData: TaskFormData, files: File[], documentMetadata: Record<string, any>) => {
    // For global tasks in work mode, create tasks without a group association
    // For personal mode, require explicit group selection
    let targetGroupId: string | null = null;

    if (isWorkMode) {
      // Global task management - create tasks without any group (null)
      targetGroupId = null;
    } else {
      // Personal mode - require explicit group selection
      targetGroupId = selectedGroupId;
      if (!targetGroupId) {
        notificationService.error('Please select a group');
        return;
      }
    }

    showLoader('Creating task...');
    try {
      // Create FormData for file uploads
      const formDataObj = new FormData();

      formDataObj.append('name', formData.name.trim());
      formDataObj.append('description', formData.description || '');
      formDataObj.append('category_id', formData.category_id);
      formDataObj.append('priority', formData.priority);
      formDataObj.append('assignee_ids', JSON.stringify(formData.assignee_ids));
      formDataObj.append('due_date', formData.due_date);
      formDataObj.append('estimated_hours', formData.estimated_hours.toString());
      formDataObj.append('approval_type', formData.approval_type);
      // Add approver IDs if multiple approvals
      if (formData.approval_type === 'multiple' && formData.approver_ids && formData.approver_ids.length > 0) {
        formDataObj.append('approver_ids', JSON.stringify(formData.approver_ids));
      }
      formDataObj.append('requires_documents', formData.requires_documents.toString());
      formDataObj.append('enable_group_chat', formData.enable_group_chat.toString());
      formDataObj.append('distribution_method', formData.distribution_method);
      formDataObj.append('point_allocation', JSON.stringify(formData.point_allocation));
      // Work descriptions are NOT stored for template tasks
      const workDescriptions = formData.allow_user_instances ? {} : formData.work_descriptions;
      formDataObj.append('work_descriptions', JSON.stringify(workDescriptions));

      // Recurrence/Scheduler fields
      formDataObj.append('is_recurring', formData.is_recurring.toString());
      formDataObj.append('recurrence_type', formData.recurrence_type);
      if (formData.recurrence_weekly_days && formData.recurrence_weekly_days.length > 0) {
        formDataObj.append('recurrence_weekly_days', JSON.stringify(formData.recurrence_weekly_days));
      }
      if (formData.recurrence_monthly_dates && formData.recurrence_monthly_dates.length > 0) {
        formDataObj.append('recurrence_monthly_dates', JSON.stringify(formData.recurrence_monthly_dates));
      }
      if (formData.recurrence_yearly_date) {
        formDataObj.append('recurrence_yearly_date', JSON.stringify(formData.recurrence_yearly_date));
      }
      if (formData.recurrence_end_date) {
        formDataObj.append('recurrence_end_date', formData.recurrence_end_date);
      }

      // User instance creation fields
      formDataObj.append('allow_user_instances', formData.allow_user_instances.toString());
      if (formData.instance_template_name) {
        formDataObj.append('instance_template_name', formData.instance_template_name);
      }

      // Add uploaded files
      files.forEach((file, index) => {
        formDataObj.append(`document_${index}`, file);
        const metadata = documentMetadata[Object.keys(documentMetadata)[index]];
        if (metadata) {
          formDataObj.append(`document_title_${index}`, metadata.title);
          formDataObj.append(`document_description_${index}`, metadata.description);
          formDataObj.append(`document_type_${index}`, metadata.document_type);
          formDataObj.append(`document_required_${index}`, metadata.is_required.toString());
        }
      });

      const response = await apiService.createTaskWithFiles(targetGroupId, formDataObj);

      if (response.status === 'success') {
        const action = response.data?.action ? {
          label: response.data.action.label,
          onClick: () => navigate(response.data.action.url)
        } : undefined;

        notificationService.success('Task created successfully', undefined, action);
        setShowAddModal(false);
        setSelectedGroupId('');
        setTaskCategories([]);
        setGroupMembers([]);
        loadTasks();
      } else {
        notificationService.error(response.message || 'Failed to create task');
      }
    } catch (error: any) {
      console.error('Error creating task:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to create task. Please try again.');
    } finally {
      hideLoader();
    }
  };

  // Load groups or hierarchy users when modal opens
  useEffect(() => {
    if (showAddModal) {
      if (isWorkMode) {
        // In work mode: Load hierarchy-based assignable users and global categories
        loadHierarchyAssignableUsers();
        // Load global categories immediately for global task management
        loadTaskCategories(undefined, 'global');
      } else {
        // In personal mode: Load groups as before
        if (groups.length === 0) {
          loadGroups();
        }
      }
    } else {
      // Reset state when modal closes
      setSelectedGroupId('');
      setTaskCategories([]);
      setGroupMembers([]);
      setHierarchyAssignableUsers([]);
    }
  }, [showAddModal, isWorkMode]);

  // Load categories and members when edit modal opens
  useEffect(() => {
    if (showEditModal && editingTask) {
      if (editingTask.group?.id) {
        // Group task - load group categories and members
        if (taskCategories.length === 0) {
          loadTaskCategories(editingTask.group.id);
        }
        if (groupMembers.length === 0) {
          loadGroupMembers(editingTask.group.id);
        }
      } else {
        // Global task - load global categories and hierarchy users
        if (taskCategories.length === 0) {
          loadTaskCategories(undefined, 'global');
        }
        if (hierarchyAssignableUsers.length === 0) {
          loadHierarchyAssignableUsers();
        }
      }
    } else {
      // Reset state when edit modal closes
      if (!showAddModal) {
        setTaskCategories([]);
        setGroupMembers([]);
        setHierarchyAssignableUsers([]);
      }
      setEditingTaskDetails(null);
      setExistingDocuments([]);
    }
  }, [showEditModal, editingTask]);

  // Load available task templates using the dedicated API endpoint
  const loadAvailableTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      // Use the dedicated API endpoint for templates (much more efficient)
      const response = await apiService.getAvailableTemplates();

      if (response.status === 'success') {
        setAvailableTemplates(response.data || []);
      } else {
        throw new Error(response.message || 'Failed to load templates');
      }
    } catch (error: any) {
      console.error('Error loading templates:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load task templates');
      setAvailableTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };


  const filteredTasks = useMemo(() => {
    // Backend now handles filtering based on activeWorkTab passed to loadTasks
    return tasks;
  }, [tasks]);

  const stats: TaskStats = calculateStats(filteredTasks, activePersonalTab, isWorkMode, activeWorkTab);

  return (
    <div className={`${styles.taskManagementContainer} ${embedded ? styles.embedded : ''}`}>
      {/* Fixed Header - Not fixed when embedded */}
      <div className={`${styles.fixedHeader} ${embedded ? styles.embeddedHeader : ''}`}>
        <div className={styles.headerContent}>
          {/* Personal Mode Tabs - Show only in personal mode */}
          {!isWorkMode ? (
            <div className={styles.personalModeTabs}>
              <button
                className={`${styles.pillTab} ${activePersonalTab === 'assigned' ? styles.pillTabActive : ''}`}
                onClick={() => handlePersonalTabChange('assigned')}
                title="Tasks assigned by someone"
              >
                Assigned
              </button>

              <button
                className={`${styles.pillTab} ${activePersonalTab === 'approvals' ? styles.pillTabActive : ''}`}
                onClick={() => handlePersonalTabChange('approvals')}
                title="Tasks requiring your approval"
              >
                Approvals
              </button>
              <div ref={initiativeMenuRef} style={{ position: 'relative' }}>
                <button
                  className={`${styles.pillTab} ${activePersonalTab === 'initiatives' ? styles.pillTabActive : ''}`}
                  onClick={(e) => {
                    if (activePersonalTab !== 'initiatives') {
                      handlePersonalTabChange('initiatives');
                    } else {
                      setShowInitiativeMenu(!showInitiativeMenu);
                    }
                    e.stopPropagation();
                  }}
                  title="Self-Initiated Tasks"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingRight: activePersonalTab === 'initiatives' ? '1rem' : '1.5rem' }}
                >
                  {activePersonalTab === 'initiatives'
                    ? (initiativeFilter === 'initiatives' ? 'Initiatives' : 'Proposals')
                    : 'Initiatives'}
                  {activePersonalTab === 'initiatives' && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '2px', transform: showInitiativeMenu ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  )}
                </button>
                {showInitiativeMenu && activePersonalTab === 'initiatives' && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    zIndex: 1000,
                    minWidth: '160px',
                    overflow: 'hidden',
                    padding: '4px'
                  }}>
                    {[
                      { value: 'initiatives', label: 'Initiatives' },
                      ...(isManagementRole ? [{ value: 'proposals', label: 'Proposals' }] : [])
                    ].map(opt => (
                      <div
                        key={opt.value}
                        onClick={() => {
                          setInitiativeFilter(opt.value as any);
                          setShowInitiativeMenu(false);
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          backgroundColor: initiativeFilter === opt.value ? '#eff6ff' : 'transparent',
                          color: initiativeFilter === opt.value ? '#2563eb' : '#374151',
                          fontSize: '0.875rem',
                          fontWeight: initiativeFilter === opt.value ? '500' : '400',
                          textAlign: 'left' // Explicitly set text alignment
                        }}
                        onMouseEnter={(e) => {
                          if (initiativeFilter !== opt.value) e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }}
                        onMouseLeave={(e) => {
                          if (initiativeFilter !== opt.value) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {isManagementRole && (
                <button
                  className={`${styles.pillTab} ${activePersonalTab === 'requests' ? styles.pillTabActive : ''}`}
                  onClick={() => handlePersonalTabChange('requests')}
                  title="Your Proposal Requests"
                >
                  Requests
                </button>
              )}

            </div>
          ) : (
            <div className={styles.personalModeTabs}>
              <button
                className={`${styles.pillTab} ${activeWorkTab === 'tasks' ? styles.pillTabActive : ''}`}
                onClick={() => setActiveWorkTab('tasks')}
                title="Manage Tasks"
              >
                Tasks
              </button>
              <button
                className={`${styles.pillTab} ${activeWorkTab === 'templates' ? styles.pillTabActive : ''}`}
                onClick={() => setActiveWorkTab('templates')}
                title="Manage Templates"
              >
                Templates
              </button>
            </div>
          )}
          <div className={styles.headerRow}>
            <div className={styles.searchWrapper}>
              <div className={searchStyles.searchBar}>
                <input
                  type="text"
                  placeholder={
                    isWorkMode
                      ? "Search"
                      : activePersonalTab === 'assigned'
                        ? "Search"
                        : activePersonalTab === 'initiatives'
                          ? "Search"
                          : "Search"
                  }
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
                <button type="submit">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div className={styles.headerActions}>
              {/* Show Create Task button only in Work Mode and if user has permissions */}
              {isWorkMode && hasEditPermissions && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  onClick={() => setShowAddModal(true)}
                  title="Create Task"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span className={styles.btnText}>Create Task</span>
                </button>
              )}
              {/* Show New Initiative button in Personal Mode (only for initiatives tab) */}
              {!isWorkMode && activePersonalTab === 'initiatives' && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                  onClick={() => {
                    setShowTemplateModal(true);
                    loadAvailableTemplates();
                  }}
                  title="New Initiative"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span className={styles.btnText}>New Initiative</span>
                </button>
              )}

              <CustomDropdown
                options={statusOptions}
                value={filters.status}
                onChange={(value: string | string[]) => handleFilterChange('status', Array.isArray(value) ? value[0] : value)}
                placeholder={activePersonalTab === 'approvals' ? 'All Approval' : 'All Status'}
                size="sm"
                variant="outlined"
                className="filter"
              />
              {/* Show priority filter only for assigned and self_assigned tabs, or work mode */}
              {(isWorkMode || activePersonalTab !== 'approvals') && (
                <CustomDropdown
                  options={priorityOptions}
                  value={filters.priority}
                  onChange={(value: string | string[]) => handleFilterChange('priority', Array.isArray(value) ? value[0] : value)}
                  placeholder="All Priority"
                  size="sm"
                  variant="outlined"
                  className="filter"
                />
              )}
              <div className={styles.dateFilterWrapper}>
                <input
                  type="date"
                  className={styles.dateFilterInput}
                  value={filters.due_date}
                  onChange={(e) => handleFilterChange('due_date', e.target.value)}
                  placeholder={activePersonalTab === 'approvals' ? 'Filter by date' : 'Due date'}
                />
                {filters.due_date && (
                  <button
                    className={styles.dateFilterClear}
                    onClick={() => handleFilterChange('due_date', '')}
                    title="Clear date filter"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </div>
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={() => loadTasks()}
                title="Refresh"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2v6h-6"></path>
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                  <path d="M3 22v-6h6"></path>
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
                </svg>
                <span className={styles.btnText}>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className={`${styles.contentSection} ${isWorkMode ? styles.contentSectionWorkMode : styles.contentSectionPersonalMode}`}>

        {!isWorkMode && activePersonalTab === 'initiatives' ? (
          <InitiativeList
            filterType={initiativeFilter}
            onProposalClick={(proposal) => handleTaskClick({ ...proposal, is_proposal: true } as any)}
          />
        ) : (
          <>
            {/* Stats */}
            <TaskStatsComponent
              stats={stats}
              isWorkMode={isWorkMode}
              activePersonalTab={activePersonalTab}
              activeWorkTab={activeWorkTab}
            />

            {/* Tasks List */}
            <TaskList
              tasks={filteredTasks}
              loading={loading}
              error={error}
              isInitialLoad={isInitialLoad}
              onTaskClick={handleTaskClick}
              onRetry={loadTasks}
              isWorkMode={isWorkMode}
              embedded={embedded}
              emptyStateTitle={
                !isWorkMode && activePersonalTab === 'requests'
                  ? "No Requests Found"
                  : isWorkMode && activeWorkTab === 'templates'
                    ? "No Templates Created"
                    : undefined
              }
              emptyStateDescription={
                !isWorkMode && activePersonalTab === 'requests'
                  ? "You haven't submitted any proposal requests yet."
                  : isWorkMode && activeWorkTab === 'templates'
                    ? "You haven't created any task templates yet."
                    : undefined
              }
              onEdit={(task: Task) => {
                // Only allow edit in work mode and if user has admin permissions
                // In personal mode, no editing allowed
                if (isWorkMode && hasEditDeletePermissions) {
                  handleEditTask(task);
                }
              }}
              onDelete={(task: Task) => {
                // Only allow delete in work mode and if user has admin permissions
                // In personal mode, no deleting allowed
                if (isWorkMode && hasEditDeletePermissions) {
                  handleDeleteTask(task);
                }
              }}
              showEditDelete={(task: Task) => {
                // In personal mode (assigned tasks tab), never show edit/delete buttons
                // Custom proposals (in requests tab) might be deletable if pending? (Not implemented yet)
                if (!isWorkMode) return false;
                // In work mode, only show edit/delete buttons if user has admin permissions
                // Only admins/is_main_admin/group admins can edit/delete tasks
                return hasEditDeletePermissions;
              }}
            />
          </>
        )}
      </div>

      {/* Create Task Modal */}
      {
        showAddModal && (
          <SidePanel
            isOpen={showAddModal}
            onClose={() => {
              setShowAddModal(false);
              setSelectedGroupId('');
              setTaskCategories([]);
              setGroupMembers([]);
              setHierarchyAssignableUsers([]);
              setSelectedAssigneesForGroup([]);
            }}
            title="Create New Task"
          >
            <div className={sidePanelStyles.form}>
              {isWorkMode ? (
                // Work mode: Show assignees directly based on hierarchy, then determine group
                isLoadingHierarchyUsers ? (
                  <div>
                    {/* Step Indicator Skeleton */}
                    <div className={taskFormStyles.stepIndicator} style={{ marginBottom: '2rem' }}>
                      {[1, 2, 3].map((stepNum, index) => (
                        <React.Fragment key={stepNum}>
                          <div className={taskFormStyles.stepItem}>
                            <div className={skeletonStyles.skeletonCustom} style={{ width: '40px', height: '40px', borderRadius: '50%', marginBottom: '0.5rem' }}>
                              <div className={skeletonStyles.skeletonShimmer}></div>
                            </div>
                            <div className={taskFormStyles.stepInfo} style={{ marginTop: '0.5rem' }}>
                              <div className={skeletonStyles.skeletonLine} style={{ width: '120px', height: '14px', marginBottom: '0.25rem' }}></div>
                            </div>
                          </div>
                          {index < 2 && (
                            <div className={taskFormStyles.stepConnector}>
                              <div className={skeletonStyles.skeletonLine} style={{ width: '100%', height: '2px' }}></div>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className={taskFormStyles.stepContent} style={{ marginTop: '2rem' }}>
                      {/* Task Name */}
                      <div className={sidePanelStyles.formGroup}>
                        <div className={skeletonStyles.skeletonLine} style={{ width: '25%', height: '16px', marginBottom: '0.5rem' }}></div>
                        <SkeletonLoader variant="custom" count={1} height="44px" />
                      </div>

                      {/* Description */}
                      <div className={sidePanelStyles.formGroup}>
                        <div className={skeletonStyles.skeletonLine} style={{ width: '30%', height: '16px', marginBottom: '0.5rem' }}></div>
                        <SkeletonLoader variant="custom" count={1} height="80px" />
                      </div>

                      {/* Category and Priority Row */}
                      <div className={sidePanelStyles.formRow}>
                        <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                          <div className={skeletonStyles.skeletonLine} style={{ width: '35%', height: '16px', marginBottom: '0.5rem' }}></div>
                          <SkeletonLoader variant="custom" count={1} height="44px" />
                        </div>
                        <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                          <div className={skeletonStyles.skeletonLine} style={{ width: '40%', height: '16px', marginBottom: '0.5rem' }}></div>
                          <SkeletonLoader variant="custom" count={1} height="44px" />
                        </div>
                      </div>

                      {/* Assign To Skeleton */}
                      <div className={sidePanelStyles.formGroup}>
                        <div className={skeletonStyles.skeletonLine} style={{ width: '20%', height: '16px', marginBottom: '0.5rem' }}></div>
                        <SkeletonLoader variant="custom" count={1} height="44px" />
                        <div style={{ marginTop: '1rem' }}>
                          <SkeletonLoader variant="custom" count={2} height="60px" />
                        </div>
                      </div>

                      {/* Due Date and Estimated Hours Row */}
                      <div className={sidePanelStyles.formRow}>
                        <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                          <div className={skeletonStyles.skeletonLine} style={{ width: '30%', height: '16px', marginBottom: '0.5rem' }}></div>
                          <SkeletonLoader variant="custom" count={1} height="44px" />
                        </div>
                        <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                          <div className={skeletonStyles.skeletonLine} style={{ width: '40%', height: '16px', marginBottom: '0.5rem' }}></div>
                          <SkeletonLoader variant="custom" count={1} height="44px" />
                        </div>
                      </div>
                    </div>

                    <div className={`${sidePanelStyles.formActions} ${taskFormStyles.stepNavigation}`} style={{ marginTop: '2rem' }}>
                      <div className={taskFormStyles.navigationLeft}>
                        <SkeletonLoader variant="custom" count={1} height="40px" width="100px" />
                      </div>
                      <div className={taskFormStyles.navigationRight}>
                        <SkeletonLoader variant="custom" count={1} height="40px" width="120px" />
                      </div>
                    </div>
                  </div>
                ) : hierarchyAssignableUsers.length > 0 ? (
                  // Show TaskForm directly with all hierarchy users
                  <TaskForm
                    groupId={selectedGroupId || ''}
                    taskCategories={taskCategories}
                    groupMembers={hierarchyAssignableUsers.map(user => ({
                      id: user.id,
                      user: {
                        id: user.id,
                        first_name: user.first_name,
                        last_name: user.last_name,
                        email: user.email,
                        get_full_name: user.full_name
                      },
                      groups: user.groups || []
                    }))}
                    isLoadingCategories={isLoadingCategories}
                    onSubmit={handleFormSubmit}
                    onCancel={() => {
                      setShowAddModal(false);
                      setSelectedGroupId('');
                      setTaskCategories([]);
                      setHierarchyAssignableUsers([]);
                      setSelectedAssigneesForGroup([]);
                    }}
                    mode="create"
                    hierarchyMode={true}
                    onAssigneeChange={(assigneeIds: string[]) => {
                      // In global task management, don't automatically determine group from assignees
                      // Global tasks should not be associated with any group
                      // Only load categories if a group was explicitly selected
                      if (selectedGroupId) {
                        loadTaskCategories(selectedGroupId);
                      } else {
                        // Load global categories when no group is selected
                        loadTaskCategories(undefined, 'global');
                      }
                    }}
                  />
                ) : (
                  <div className={sidePanelStyles.formGroup}>
                    <div className={sidePanelStyles.formHelp}>
                      <i className="bi bi-info-circle" style={{ color: 'var(--info)', marginRight: '0.3rem' }}></i>
                      No assignable users found based on your hierarchy position.
                    </div>
                  </div>
                )
              ) : (
                // Personal mode: Original group selection flow
                <>
                  {/* Group Selection */}
                  <div className={sidePanelStyles.formGroup}>
                    <label className={sidePanelStyles.formLabel}>
                      Group <span className="required">*</span>
                    </label>
                    <CustomDropdown
                      options={[
                        { value: '', label: 'Select a group' },
                        ...groups.map(group => ({ value: group.id, label: group.name }))
                      ]}
                      value={selectedGroupId}
                      onChange={(value) => handleGroupChange(Array.isArray(value) ? value[0] : value)}
                      placeholder="Select a group"
                      disabled={isLoadingGroups}
                      size="md"
                      variant="outlined"
                      className={customDropdownStyles.sizeFull}
                    />
                  </div>

                  {/* Task Form Component */}
                  {selectedGroupId && (
                    <TaskForm
                      groupId={selectedGroupId}
                      taskCategories={taskCategories}
                      groupMembers={groupMembers}
                      isLoadingCategories={isLoadingCategories}
                      onSubmit={handleFormSubmit}
                      onCancel={() => {
                        setShowAddModal(false);
                        setSelectedGroupId('');
                        setTaskCategories([]);
                        setGroupMembers([]);
                      }}
                      mode="create"
                    />
                  )}

                  {!selectedGroupId && (
                    <div className={sidePanelStyles.formGroup}>
                      <div className={sidePanelStyles.formHelp}>
                        <i className="bi bi-info-circle" style={{ color: 'var(--info)', marginRight: '0.3rem' }}></i>
                        Please select a group to create a task.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </SidePanel>
        )
      }

      {/* Task Instance Form Modal for Personal Mode */}
      <TaskInitiationForm
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
        }}
        onSuccess={() => {
          loadTasks();
        }}
        availableTemplates={availableTemplates}
        isLoadingTemplates={isLoadingTemplates}
        onLoadTemplates={loadAvailableTemplates}
        enableCustomProposal={true}
      />

      {/* Edit Task Modal */}
      {
        showEditModal && editingTask && (
          <SidePanel
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingTask(null);
              setEditingTaskDetails(null);
              setExistingDocuments([]);
              setTaskCategories([]);
              setGroupMembers([]);
              setSelectedGroupId('');
            }}
            title="Edit Task"
          >
            <div className={sidePanelStyles.form}>
              {isLoadingTaskDetails ? (
                <div className={`${sidePanelStyles.form} ${taskFormStyles.formContainer}`} style={{ pointerEvents: 'none' }}>
                  {/* Step Indicator Skeleton */}
                  <div className={taskFormStyles.stepIndicator}>
                    {[1, 2, 3].map((stepNum, index) => (
                      <React.Fragment key={stepNum}>
                        <div className={taskFormStyles.stepItem}>
                          <div className={skeletonStyles.skeletonCustom} style={{ width: '40px', height: '40px', borderRadius: '50%', marginBottom: '0.5rem' }}>
                            <div className={skeletonStyles.skeletonShimmer}></div>
                          </div>
                          <div className={taskFormStyles.stepInfo} style={{ marginTop: '0.5rem' }}>
                            <div className={skeletonStyles.skeletonLine} style={{ width: '120px', height: '14px', marginBottom: '0.25rem' }}></div>
                          </div>
                        </div>
                        {index < 2 && (
                          <div className={taskFormStyles.stepConnector}>
                            <div className={skeletonStyles.skeletonLine} style={{ width: '100%', height: '2px' }}></div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Form Content Skeleton */}
                  <div className={taskFormStyles.stepContent} style={{ marginTop: '2rem' }}>
                    {/* Task Name */}
                    <div className={sidePanelStyles.formGroup}>
                      <div className={skeletonStyles.skeletonLine} style={{ width: '25%', height: '16px', marginBottom: '0.5rem' }}></div>
                      <SkeletonLoader variant="custom" count={1} height="44px" />
                    </div>

                    {/* Description */}
                    <div className={sidePanelStyles.formGroup}>
                      <div className={skeletonStyles.skeletonLine} style={{ width: '30%', height: '16px', marginBottom: '0.5rem' }}></div>
                      <SkeletonLoader variant="custom" count={1} height="80px" />
                    </div>

                    {/* Category and Priority Row */}
                    <div className={sidePanelStyles.formRow}>
                      <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                        <div className={skeletonStyles.skeletonLine} style={{ width: '35%', height: '16px', marginBottom: '0.5rem' }}></div>
                        <SkeletonLoader variant="custom" count={1} height="44px" />
                      </div>
                      <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                        <div className={skeletonStyles.skeletonLine} style={{ width: '40%', height: '16px', marginBottom: '0.5rem' }}></div>
                        <SkeletonLoader variant="custom" count={1} height="44px" />
                      </div>
                    </div>

                    {/* Due Date and Estimated Hours Row */}
                    <div className={sidePanelStyles.formRow}>
                      <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                        <div className={skeletonStyles.skeletonLine} style={{ width: '30%', height: '16px', marginBottom: '0.5rem' }}></div>
                        <SkeletonLoader variant="custom" count={1} height="44px" />
                      </div>
                      <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                        <div className={skeletonStyles.skeletonLine} style={{ width: '40%', height: '16px', marginBottom: '0.5rem' }}></div>
                        <SkeletonLoader variant="custom" count={1} height="44px" />
                      </div>
                    </div>
                  </div>

                  {/* Step Navigation Skeleton */}
                  <div className={`${sidePanelStyles.formActions} ${taskFormStyles.stepNavigation}`} style={{ marginTop: '2rem' }}>
                    <div className={taskFormStyles.navigationLeft}>
                      <SkeletonLoader variant="custom" count={1} height="40px" width="100px" />
                    </div>
                    <div className={taskFormStyles.navigationRight}>
                      <SkeletonLoader variant="custom" count={1} height="40px" width="120px" />
                    </div>
                  </div>
                </div>
              ) : editingTaskDetails ? (
                <TaskForm
                  groupId={editingTask.group?.id || ''}
                  taskCategories={taskCategories}
                  groupMembers={
                    editingTask.group
                      ? groupMembers
                      : (() => {
                        // For global tasks, merge hierarchy users with assignees from task details
                        // This ensures all assigned users are available even if not in hierarchy
                        console.log('Calculating groupMembers for Global Task:', {
                          hierarchyCount: hierarchyAssignableUsers.length,
                          hierarchyUsers: hierarchyAssignableUsers,
                          editingTaskAssignees: editingTaskDetails.assignees
                        });
                        const hierarchyMembers = hierarchyAssignableUsers.map(user => ({
                          id: user.id,
                          user: {
                            id: user.id,
                            first_name: user.first_name,
                            last_name: user.last_name,
                            email: user.email,
                            get_full_name: user.full_name
                          },
                          groups: user.groups || []
                        }));

                        // Create a map of existing members by user ID
                        const membersMap = new Map<string, any>();
                        hierarchyMembers.forEach(member => {
                          const userId = String(member.user?.id || member.id);
                          membersMap.set(userId, member);
                        });

                        // Add assignees from task details that aren't already in the list
                        if (editingTaskDetails.assignees && Array.isArray(editingTaskDetails.assignees)) {
                          console.log('Merging assignees from task details:', {
                            assignees: editingTaskDetails.assignees,
                            hierarchyMembersCount: hierarchyMembers.length
                          });

                          editingTaskDetails.assignees.forEach((assignee: any) => {
                            const userId = String(assignee.user?.id || assignee.id);
                            if (!membersMap.has(userId) && assignee.user) {
                              console.log('Adding assignee to members map:', {
                                userId,
                                assigneeName: `${assignee.user.first_name || ''} ${assignee.user.last_name || ''}`.trim()
                              });
                              membersMap.set(userId, {
                                id: assignee.id || assignee.user.id,
                                user: {
                                  id: assignee.user.id,
                                  first_name: assignee.user.first_name,
                                  last_name: assignee.user.last_name,
                                  email: assignee.user.email,
                                  get_full_name: assignee.user.full_name || `${assignee.user.first_name || ''} ${assignee.user.last_name || ''}`.trim()
                                },
                                groups: []
                              });
                            } else {
                              console.log('Assignee already in map or missing user:', {
                                userId,
                                alreadyInMap: membersMap.has(userId),
                                hasUser: !!assignee.user
                              });
                            }
                          });
                        }

                        const mergedMembers = Array.from(membersMap.values());
                        console.log('Final merged members for TaskForm:', {
                          totalCount: mergedMembers.length,
                          members: mergedMembers.map(m => ({
                            id: m.user?.id || m.id,
                            name: `${m.user?.first_name || ''} ${m.user?.last_name || ''}`.trim()
                          }))
                        });

                        return mergedMembers;
                      })()
                  }
                  assigneesData={editingTaskDetails.assignees || []}
                  isLoadingCategories={isLoadingCategories}
                  onSubmit={handleUpdateTask}
                  onCancel={() => {
                    setShowEditModal(false);
                    setEditingTask(null);
                    setEditingTaskDetails(null);
                    setExistingDocuments([]);
                    setTaskCategories([]);
                    setGroupMembers([]);
                    setHierarchyAssignableUsers([]);
                    setSelectedGroupId('');
                  }}
                  mode="edit"
                  hierarchyMode={!editingTask.group}
                  initialData={{
                    name: editingTaskDetails.name || '',
                    description: editingTaskDetails.description || '',
                    category_id: editingTaskDetails.category?.id || '',
                    priority: editingTaskDetails.priority || 'medium',
                    assignee_ids: (() => {
                      const ids = editingTaskDetails.assignees?.map((a: any) => {
                        // Normalize to string for consistent comparison
                        const userId = a.user?.id || a.id;
                        return userId ? String(userId) : null;
                      }).filter((id: string | null) => id !== null) || [];

                      console.log('Extracted assignee_ids from task details:', {
                        assignees: editingTaskDetails.assignees,
                        extractedIds: ids,
                        assigneeCount: editingTaskDetails.assignees?.length || 0
                      });

                      return ids;
                    })(),
                    due_date: editingTaskDetails.due_date ? editingTaskDetails.due_date.split('T')[0] : '',
                    estimated_hours: editingTaskDetails.estimated_hours || 0,
                    status: editingTaskDetails.status || 'pending',
                    approval_type: editingTaskDetails.approval_type || (editingTaskDetails.requires_approval ? 'single' : 'none'),
                    approver_ids: editingTaskDetails.approver_ids || [],
                    approvers: editingTaskDetails.approvers || [], // Pass full approver details
                    requires_documents: editingTaskDetails.requires_documents || false,
                    enable_group_chat: editingTaskDetails.enable_group_chat || false,
                    distribution_method: editingTaskDetails.distribution_method || 'equal',
                    direct_assignment_type: editingTaskDetails.direct_assignment_type || 'single',
                    base_points: editingTaskDetails.base_points || 0,
                    team_lead_points: editingTaskDetails.team_lead_points || 0,
                    point_allocation: editingTaskDetails.point_allocation || {},
                    work_descriptions: editingTaskDetails.assignees?.reduce((acc: any, a: any) => {
                      const userId = a.user?.id || a.id;
                      if (userId && a.work_description) {
                        // Normalize key to string for consistent lookup
                        acc[String(userId)] = a.work_description;
                      }
                      return acc;
                    }, {}) || {},
                    is_recurring: editingTaskDetails.is_recurring || false,
                    recurrence_type: editingTaskDetails.recurrence_type || 'none',
                    recurrence_weekly_days: editingTaskDetails.recurrence_weekly_days || [],
                    recurrence_monthly_dates: editingTaskDetails.recurrence_monthly_dates || [],
                    recurrence_yearly_date: editingTaskDetails.recurrence_yearly_date || { month: 1, day: 1 },
                    recurrence_end_date: editingTaskDetails.recurrence_end_date ? editingTaskDetails.recurrence_end_date.split('T')[0] : '',
                    allow_user_instances: editingTaskDetails.allow_user_instances || false,
                    instance_template_name: editingTaskDetails.instance_template_name || '',
                  }}
                  existingDocuments={existingDocuments}
                  onDeleteDocument={async (documentId: string) => {
                    if (!editingTask?.group?.id) return;
                    try {
                      const response = await apiService.deleteTaskDocument(editingTask.group.id, editingTask.id, documentId);
                      if (response.status === 'success') {
                        setExistingDocuments(prev => prev.filter(doc => doc.id !== documentId));
                        notificationService.success('Document deleted successfully');
                      } else {
                        notificationService.error(response.message || 'Failed to delete document');
                      }
                    } catch (error: any) {
                      console.error('Error deleting document:', error);
                      notificationService.error('Failed to delete document');
                    }
                  }}
                  onViewDocument={(doc: any) => {
                    if (doc.file_url) {
                      window.open(doc.file_url, '_blank');
                    }
                  }}
                  onDownloadDocument={(doc: any) => {
                    if (doc.file_url) {
                      const link = document.createElement('a');
                      link.href = doc.file_url;
                      link.download = doc.filename || doc.title;
                      link.click();
                    }
                  }}
                />
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <p>Failed to load task details. Please try again.</p>
                </div>
              )}
            </div>
          </SidePanel>
        )
      }

      {/* Floating Working Mode Button - Only show if user has add/edit/delete permissions and not embedded */}
      {
        hasEditPermissions && !embedded && (
          <FloatingWorkingMode
            position="bottom-right"
            activeLabel="Work Mode"
            inactiveLabel="Personal"
          />
        )
      }

      {/* Proposal Details Panel */}
      {selectedProposalId && (
        <ProposalDetailsPanel
          proposalId={selectedProposalId}
          isOpen={showProposalPanel}
          onClose={() => {
            setShowProposalPanel(false);
            setSelectedProposalId(null);
          }}
          onUpdate={() => loadTasks(true)}
        />
      )}
    </div >
  );
};

export default TaskManagement;

